import React, { forwardRef } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends HTMLMotionProps<"button"> {
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
    size?: 'sm' | 'md' | 'lg';
    fullWidth?: boolean;
    isLoading?: boolean;
    icon?: React.ReactNode;
    iconPosition?: 'start' | 'end';
    children?: React.ReactNode;
}

// Create a wrapper component to handle the ref forwarding and props
const MotionWrapper = motion.button;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
    children,
    className,
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    isLoading = false,
    icon,
    iconPosition = 'start',
    disabled,
    ...props
}, ref) => {
    const baseStyles = 'font-display font-semibold rounded-xl transition-all duration-200 flex items-center justify-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500';

    const variants = {
        primary: 'bg-primary-500 hover:bg-primary-600 text-white border-none shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 hover:-translate-y-px',
        secondary: 'bg-secondary-100 hover:bg-secondary-200 text-secondary-900 border-none hover:-translate-y-px',
        outline: 'bg-transparent border-2 border-primary-500 text-primary-500 hover:bg-primary-50 hover:-translate-y-px',
        ghost: 'bg-transparent hover:bg-secondary-50 text-secondary-600 hover:text-primary-600 border-none shadow-none',
        danger: 'bg-danger-500 hover:bg-danger-600 text-white border-none shadow-lg shadow-danger-500/30 hover:shadow-xl hover:shadow-danger-500/40 hover:-translate-y-px',
    };

    const sizes = {
        sm: 'h-8 px-3 text-sm gap-2',
        md: 'h-10 px-5 text-base gap-2',
        lg: 'h-12 px-8 text-lg gap-3',
    };

    const classes = twMerge(
        baseStyles,
        variants[variant],
        sizes[size],
        fullWidth ? 'w-full' : '',
        className
    );

    return (
        <MotionWrapper
            ref={ref}
            whileTap={{ scale: 0.98 }}
            className={classes}
            disabled={disabled || isLoading}
            {...props}
        >
            {isLoading && <Loader2 className="animate-spin" size={18} />}
            {!isLoading && icon && iconPosition === 'start' && <span>{icon}</span>}
            {children}
            {!isLoading && icon && iconPosition === 'end' && <span>{icon}</span>}
        </MotionWrapper>
    );
});

Button.displayName = 'Button';
